import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CalendarDaysIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  WrenchScrewdriverIcon,
  Cog6ToothIcon,
  MapPinIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

// Direction options
const DIRECTION_OPTIONS = [
  { value: '', label: 'Select direction...' },
  { value: 'NB', label: 'Northbound' },
  { value: 'SB', label: 'Southbound' },
  { value: 'EB', label: 'Eastbound' },
  { value: 'WB', label: 'Westbound' },
];

// Lane options (1-8)
const LANE_OPTIONS = [
  { value: '', label: 'Select lane...' },
  { value: '1', label: 'Lane 1' },
  { value: '2', label: 'Lane 2' },
  { value: '3', label: 'Lane 3' },
  { value: '4', label: 'Lane 4' },
  { value: '5', label: 'Lane 5' },
  { value: '6', label: 'Lane 6' },
  { value: '7', label: 'Lane 7' },
  { value: '8', label: 'Lane 8' },
];

// Helper to get direction display label
const getDirectionLabel = (value) => {
  const option = DIRECTION_OPTIONS.find(opt => opt.value === value);
  return option?.label || value || 'Not specified';
};

// Helper to get lane display label
const getLaneLabel = (value) => {
  if (!value) return 'Not specified';
  return `Lane ${value}`;
};

// Custom Select Component
const CustomSelect = ({ value, onChange, options, placeholder = 'Select...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption?.value ? selectedOption.label : placeholder;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full px-3 py-2 text-left text-sm bg-white border rounded-sm
          flex items-center justify-between
          transition-colors
          ${isOpen 
            ? 'border-red-500 ring-2 ring-red-500' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${!selectedOption?.value ? 'text-gray-400' : 'text-gray-900'}
        `}
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDownIcon 
          className={`h-4 w-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-60 overflow-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`
                w-full px-3 py-2 text-left text-sm transition-colors
                ${option.value === value 
                  ? 'bg-red-50 text-red-700' 
                  : 'text-gray-700 hover:bg-gray-50'
                }
                ${!option.value ? 'text-gray-400' : ''}
              `}
            >
              <div className="flex items-center justify-between">
                <span>{option.label}</span>
                {option.value === value && option.value && (
                  <CheckIcon className="h-4 w-4 text-red-600" />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Collapsible section component
const MetadataSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-gray-500" />}
          <span className="font-medium text-sm text-gray-700">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDownIcon className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-3 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

// Metadata field display component
const MetadataField = ({ label, value, mono = false }) => {
  const displayValue = value || '—';
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>
        {displayValue}
      </dd>
    </div>
  );
};

// Helper to format station values
const formatStation = (stationFt) => {
  if (stationFt === null || stationFt === undefined) return '—';
  const hundreds = Math.floor(stationFt / 100);
  const remainder = stationFt % 100;
  return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
};

const SessionDetails = ({
  session,
  isEditing,
  editValues,
  saving,
  handleEdit,
  handleCancelEdit,
  handleSaveEdit,
  handleInputChange,
  setShowDeleteModal,
  formatDate,
  formatDateForInput,
}) => {
  const navigate = useNavigate();
  
  // Parse metadata from session
  const meta = session?.meta || {};

  return (
    <div className="bg-white rounded-sm shadow-sm relative flex-1 flex flex-col min-h-0">
      {/* Header - matches FixedIntervalChart style */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Session Details</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              View and edit session information
            </p>
          </div>
          {!isEditing ? (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleEdit}
                className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-sm hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded-sm hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="inline-flex items-center px-3 py-1.5 text-sm text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4 mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-sm hover:bg-gray-50"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable body — form fields + metadata stack together */}
      <div className="flex-1 overflow-auto min-h-0">
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Row 1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Name</label>
            {isEditing ? (
              <input
                type="text"
                value={editValues.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter session name"
              />
            ) : (
              <p className="text-sm text-gray-900 font-medium truncate" title={session.name || session.filename || 'Unnamed Session'}>{session.name || session.filename || 'Unnamed Session'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Run Date</label>
            {isEditing ? (
              <input
                type="date"
                value={formatDateForInput(editValues.run_date)}
                onChange={(e) => handleInputChange('run_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            ) : (
              <p className="text-sm text-gray-900">{session.run_date ? formatDate(session.run_date) : 'Not specified'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Operator</label>
            {isEditing ? (
              <input
                type="text"
                value={editValues.operator}
                onChange={(e) => handleInputChange('operator', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter operator name"
              />
            ) : (
              <p className="text-sm text-gray-900">{session.operator || meta.operator || 'Not specified'}</p>
            )}
          </div>

          {/* Row 2 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
            {isEditing ? (
              <CustomSelect
                value={editValues.direction || ''}
                onChange={(value) => handleInputChange('direction', value)}
                options={DIRECTION_OPTIONS}
                placeholder="Select direction..."
              />
            ) : (
              <p className="text-sm text-gray-900">{getDirectionLabel(session.direction)}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lane</label>
            {isEditing ? (
              <CustomSelect
                value={editValues.lane_no || ''}
                onChange={(value) => handleInputChange('lane_no', value)}
                options={LANE_OPTIONS}
                placeholder="Select lane..."
              />
            ) : (
              <p className="text-sm text-gray-900">{getLaneLabel(session.lane_no)}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Date</label>
            <div className="flex items-center text-sm text-gray-900">
              <CalendarDaysIcon className="h-4 w-4 mr-2" />
              {formatDate(session.upload_timestamp || session.created_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Metadata Accordion Sections */}
      <div className="px-6 pb-6 space-y-2">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Run Metadata</h4>
        
        {/* Equipment & Certification */}
        <MetadataSection 
          title="Equipment & Certification" 
          icon={WrenchScrewdriverIcon}
          defaultOpen={true}
        >
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetadataField label="Profiler Model" value={meta.model_num || session.profiler_model} />
            <MetadataField label="Serial Number" value={meta.serial_num} mono />
            <MetadataField label="Certification #" value={meta.certification_num} mono />
            <MetadataField label="Cert. Expiration" value={meta.certification_date} />
            <MetadataField label="Company" value={meta.company} />
            <MetadataField label="Job" value={meta.job} />
          </dl>
        </MetadataSection>

        {/* Collection Parameters */}
        <MetadataSection 
          title="Collection Parameters" 
          icon={Cog6ToothIcon}
        >
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetadataField label="Sample Rate" value={meta.sample_rate} />
            <MetadataField label="Collection Speed" value={meta['collection_speed(mph)'] ? `${parseFloat(meta['collection_speed(mph)']).toFixed(1)} mph` : null} />
            <MetadataField label="Collection Date" value={meta.collection_date} />
            <MetadataField label="Collection Time" value={meta.collection_time !== 'Time:' ? meta.collection_time : null} />
            <MetadataField label="Collection Version" value={meta.collection_version} mono />
            <MetadataField label="Collection Filter" value={meta.collection_filter} />
            <MetadataField label="Unit System" value={meta.profile_unit_system} />
          </dl>
        </MetadataSection>

        {/* Station Range */}
        <MetadataSection 
          title="Station Range" 
          icon={MapPinIcon}
        >
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetadataField 
              label="Start Station" 
              value={formatStation(session.start_station_ft)} 
              mono 
            />
            <MetadataField 
              label="End Station" 
              value={formatStation(session.end_station_ft)} 
              mono 
            />
            <MetadataField 
              label="Stationing Direction" 
              value={meta.is_reverse_stationing ? 'Reverse Stationing' : 'Forward Stationing'} 
            />
            <MetadataField 
              label="Pre/Post Run Length" 
              value={meta['pre/post_run_length']} 
            />
            <MetadataField label="Highway" value={meta.highway} />
            <MetadataField label="County" value={meta.county} />
          </dl>
        </MetadataSection>

        {/* Analysis Settings */}
        <MetadataSection 
          title="Analysis Settings" 
          icon={AdjustmentsHorizontalIcon}
        >
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetadataField label="Analysis Version" value={meta.analysis_version} mono />
            <MetadataField label="Reduction Length" value={meta['reduction_length(ft.)'] ? `${meta['reduction_length(ft.)']} ft` : null} />
            <MetadataField label="Horizontal Scale" value={meta.horizontal_scale} />
            <MetadataField label="Paper Factor" value={meta.paper_factor} />
            <MetadataField label="High-Pass Filter" value={meta['high-pass_filter(ft.)'] ? `${meta['high-pass_filter(ft.)']} ft` : null} />
            <MetadataField label="Low-Pass Filter" value={meta['low-pass_filter(ft.)'] ? `${meta['low-pass_filter(ft.)']} ft` : null} />
            <MetadataField label="Horizontal Calibration" value={meta.horizontal_calibration} />
            <MetadataField label="Calibration Divisor" value={meta['horizontal_cal._divisor']} />
          </dl>
        </MetadataSection>

        {/* Comments (if present) */}
        {meta.comments && (
          <MetadataSection title="Comments" defaultOpen={true}>
            <p className="text-sm text-gray-700">{meta.comments}</p>
          </MetadataSection>
        )}
      </div>
      </div>
    </div>
  );
};

export default SessionDetails;